import { Button } from '@headlessui/react'
import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { PenLine, SquareCheck } from 'lucide-react'
import { Transition } from '@headlessui/react'
import { NoteFormValues } from 'types/crm/notes'
import { TaskFormValues } from "types/crm/task";
import NotesTab, { NoteQueryResult } from "../Notes/NotesTab";
import TasksTab, { TaskQueryResult } from "../Tasks/TaskTab";
import AllActivityTab, {
  ActivityQueryResult,
} from "../AllActivities/AllActivityTab";
import { CrmCommentHooks } from 'types/crm/comments';
import { useRecordAccess } from 'context/crm/RecordAccessContext';

interface ActivityAdapter {
  useActivityQuery: (entityUuid: string) => ActivityQueryResult;
  useNotesQuery: (entityUuid: string) => NoteQueryResult;
  useCreateNote: () => any;
  buildNotePayload: (
    entityUuid: string,
    values: NoteFormValues,
  ) => any;
  /** Mutation hook for editing a note. Same shape as useCreateNote. */
  useUpdateNote: () => any;
  /** Builds the update payload from the entity uuid, the note uuid being edited, and the new form values. */
  buildUpdateNotePayload: (
    entityUuid: string,
    noteUuid: string,
    values: NoteFormValues,
  ) => any;
  /** Mutation hook for deleting a note. */
  useDeleteNote: () => any;
  /** Builds the delete payload from the entity uuid and note uuid — key name (contactUuid/dealUuid/companyUuid) varies per entity. */
  buildDeleteNotePayload: (entityUuid: string, noteUuid: string) => any;
  useTasksQuery: (entityUuid: string) => TaskQueryResult;
  /** Overdue tasks, pinned at the top of All Activity. */
  useOverdueTasksQuery: (entityUuid: string) => TaskQueryResult;
  useCreateTask: () => any;
  buildTaskPayload: (entityUuid: string, values: TaskFormValues) => any;
  /** Mutation hook for editing a task. Same shape as useCreateTask. */
  useUpdateTask: () => any;
  /** Builds the update payload from the entity uuid, the task uuid being edited, and the new form values. */
  buildUpdateTaskPayload: (
    entityUuid: string,
    taskUuid: string,
    values: TaskFormValues,
  ) => any;
  /** Mutation hook for deleting a task. */
  useDeleteTask: () => any;
  /** Builds the delete payload from the entity uuid and task uuid — key name (contactUuid/dealUuid/companyUuid) varies per entity. */
  buildDeleteTaskPayload: (entityUuid: string, taskUuid: string) => any;
  noteCommentHooks: CrmCommentHooks;
  taskCommentHooks: CrmCommentHooks;
}

interface ActivityProps extends ActivityAdapter {
  entityUuid: string;
}

const ACTIVITY_TABS = [
  { key: "all", label: "All Activity", searchPlaceholder: "Search activities..." },
  { key: "notes", label: "Notes", searchPlaceholder: "Search notes..." },
  { key: "tasks", label: "Tasks", searchPlaceholder: "Search tasks..." },
];
const VALID_TABS = ["all", "notes", "tasks"] as const;
type TabKey = (typeof VALID_TABS)[number];

const Activity = ({ entityUuid, ...adapter }: ActivityProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * RBAC (section 13): "Add Note" and "Create Task" are modification actions and
   * must be hidden for view-only users. Tabs, lists, activity feed and search all
   * stay available (sections 4, 14) — viewing is never restricted.
   */
  const { canAddNote, canCreateTask } = useRecordAccess();
  const showActionBar = canAddNote || canCreateTask;

  const tabParam = searchParams.get("tab");
  // A deep-link to a specific task (?taskUuid=...) always wins the tasks tab,
  // even if ?tab is missing or points elsewhere.
  const highlightTaskUuid = searchParams.get("taskUuid");
  const activeTab: TabKey = highlightTaskUuid
    ? "tasks"
    : VALID_TABS.includes(tabParam as TabKey)
      ? (tabParam as TabKey)
      : "all";

  const [noteTrigger, setNoteTrigger] = React.useState(0);
  const [taskTrigger, setTaskTrigger] = React.useState(0);

  const setActiveTab = (tab: TabKey) => {
    // preserve any other existing query params, only touch "tab".
    // Switching tabs manually clears a pending taskUuid highlight so it
    // doesn't keep forcing the tasks tab back open.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        next.delete("taskUuid");
        return next;
      },
      { replace: true } // don't spam browser history on every tab click
    );
  };

  // Once the target row has been scrolled to and highlighted, clear
  // taskUuid from the URL so it doesn't re-trigger on refresh/back-nav.
  const clearTaskHighlight = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("taskUuid");
        return next;
      },
      { replace: true }
    );
  };

  // Opens a task from the overdue list in the Tasks tab — the same deep link
  // (?taskUuid=) that scrolls to and highlights it.
  const openTask = (taskUuid: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "tasks");
        next.set("taskUuid", taskUuid);
        return next;
      },
      { replace: true }
    );
  };

  // const searchPlaceholder =
  //   ACTIVITY_TABS.find((tab) => tab.key === activeTab)?.searchPlaceholder ??
  //   "Search activities...";

  const handleAddNote = () => {
    if (!canAddNote) return;
    setActiveTab("notes");
    setNoteTrigger((prev) => prev + 1);
  };

  const handleCreateTask = () => {
    if (!canCreateTask) return;
    setActiveTab("tasks");
    setTaskTrigger((prev) => prev + 1);
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0 rounded-l-xl bg-primary">
      {/* Hide the whole bar when neither action is permitted, otherwise a
          view-only user gets an empty bordered strip above the tabs. */}
      {showActionBar && (
        <div className="shrink-0 flex gap-3 border-b border-primary-subtle p-3">
          {canAddNote && (
            <Button
              className={`flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "notes"
                  ? "bg-emphasis text-neutral hover:opacity-90"
                  : "border border-primary-subtle text-neutral hover:bg-primary-surface"
              }`}
              onClick={handleAddNote}
            >
              <PenLine className="w-4 h-4" />
              Add Note
            </Button>
          )}
          {canCreateTask && (
            <Button
              className={`flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "tasks"
                  ? "bg-emphasis text-neutral hover:opacity-90 "
                  : "border border-primary-subtle text-neutral hover:bg-primary-surface"
              }`}
              onClick={handleCreateTask}
            >
              <SquareCheck className="w-4 h-4" />
              Create task
            </Button>
          )}
        </div>
      )}

      <div className="shrink-0 flex items-center justify-between p-4 border-b border-primary-subtle">
        <div className="flex gap-6">
          {ACTIVITY_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as TabKey)}
              className={`relative pb-1 text-sm font-medium transition-colors duration-200 ${activeTab === tab.key
                ? "text-neutral"
                : "text-neutral-faded hover:text-neutral"
                }`}
            >
              {tab.label}
              <span
                className={`absolute left-0 right-0 -bottom-[1px] h-[3px] rounded-full bg-emphasis transition-transform duration-200 ease-out ${activeTab === tab.key ? "scale-x-100" : "scale-x-0"
                  }`}
              />
            </button>
          ))}
        </div>

        {/* Search temporarily disabled until search is wired up.
            NOTE (section 14): search is a view action — when it is re-enabled it
            must NOT be gated on edit access.
        <div className="relative">
          <Search className="absolute disabled:bg-none left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-faded" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            className="bg-primary-surface border border-primary-subtle rounded-lg pl-9 pr-3 py-1.5 text-sm text-neutral-faded placeholder-placeholder focus:outline-none focus:border-emphasis w-56"
          />
        </div>
        */}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 p-4">
        <Transition
          // key={activeTab}
          show={true}
          appear={true}
          as="div"
          enter="transition ease-out duration-300"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
        >
          <AllActivityTab
            isActive={activeTab === "all"}
            entityUuid={entityUuid}
            useActivityQuery={adapter.useActivityQuery}
            useOverdueTasksQuery={adapter.useOverdueTasksQuery}
            onOpenTask={openTask}
          />
          <NotesTab
            isActive={activeTab === "notes"}
            entityUuid={entityUuid}
            openTrigger={noteTrigger}
            useNotesQuery={adapter.useNotesQuery}
            useCreateNote={adapter.useCreateNote}
            buildCreatePayload={adapter.buildNotePayload}
            useUpdateNote={adapter.useUpdateNote}
            buildUpdatePayload={adapter.buildUpdateNotePayload}
            useDeleteNote={adapter.useDeleteNote}
            buildDeletePayload={adapter.buildDeleteNotePayload}
            commentHooks={adapter.noteCommentHooks}
          />
          <TasksTab
            isActive={activeTab === "tasks"}
            entityUuid={entityUuid}
            openTrigger={taskTrigger}
            useTasksQuery={adapter.useTasksQuery}
            useCreateTask={adapter.useCreateTask}
            buildCreatePayload={adapter.buildTaskPayload}
            useUpdateTask={adapter.useUpdateTask}
            buildUpdatePayload={adapter.buildUpdateTaskPayload}
            useDeleteTask={adapter.useDeleteTask}
            buildDeletePayload={adapter.buildDeleteTaskPayload}
            commentHooks={adapter.taskCommentHooks}
            highlightTaskUuid={highlightTaskUuid}
            onTaskHighlighted={clearTaskHighlight}

          />
        </Transition>
      </div>
    </div>
  );
};

export default Activity